import { Icon, Typography } from "@/components/atoms";
import { TourBlock } from "./types";

/**
 * Renderers for the `TourBlock` union — one per member, dispatched by `TourBlockContent`.
 *
 * Kept in one file rather than a folder of one-component files: none of these is reusable on its own,
 * they only make sense inside a slide, and reading the union next to its five renderings is how you
 * check the set is still complete.
 */

const ParagraphBlockContent = ({ text }: { text: string }) => (
  <Typography variant="paragraph" className="text-neutral-dark-gray">
    {text}
  </Typography>
);

const CalloutBlockContent = ({ text }: { text: string }) => (
  <div className="flex items-start gap-3 rounded-lg border border-warning bg-warning-light p-4">
    {/* LampCharge, not Lamp: IconSax carries an explicit allowlist and Lamp is not on it, so it would
        render nothing. PlayCircle WAS added to that allowlist, because the design names it;
        LampCharge is close enough to the design's bulb to leave alone. */}
    <Icon name="LampCharge" variant="Bold" size="lg" className="mt-0.5 shrink-0 text-warning" aria-hidden />
    <Typography variant="paragraph" className="text-neutral-black">
      {text}
    </Typography>
  </div>
);

/**
 * Two shapes, chosen by the data rather than a flag: an item with a description is a titled card in a
 * bordered box (the operator deck), an item without one is a bare ticked line (the EA deck). Making it
 * a prop would let a caller pick the boxed treatment and then supply nothing to put in the box.
 */
const ChecklistBlockContent = ({ items }: { items: Array<{ title: string; description?: string }> }) => (
  <ul className="flex flex-col gap-3">
    {items.map((item) =>
      item.description ? (
        <li key={item.title} className="flex items-start gap-3 rounded-lg border border-neutral-mid-gray p-3">
          <Icon name="TickCircle" variant="Bold" size="md" className="mt-0.5 shrink-0 text-success" aria-hidden />
          <div className="flex flex-col gap-0.5">
            <Typography variant="action">{item.title}</Typography>
            <Typography variant="paragraph" className="text-neutral-dark-gray">
              {item.description}
            </Typography>
          </div>
        </li>
      ) : (
        <li key={item.title} className="flex items-start gap-2.5">
          <Icon name="TickCircle" variant="Bold" size="md" className="mt-0.5 shrink-0 text-success" aria-hidden />
          <Typography variant="paragraph" className="text-neutral-black">
            {item.title}
          </Typography>
        </li>
      )
    )}
  </ul>
);

const NumberedBlockContent = ({ items }: { items: Array<{ title: string; description: string }> }) => (
  <ol className="flex flex-col gap-3">
    {items.map((item, index) => (
      <li key={item.title} className="flex items-start gap-3 rounded-lg border border-neutral-mid-gray p-3">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary" aria-hidden>
          <Typography variant="small" className="leading-none text-white">
            {index + 1}
          </Typography>
        </span>
        <div className="flex flex-col gap-0.5">
          <Typography variant="action">{item.title}</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            {item.description}
          </Typography>
        </div>
      </li>
    ))}
  </ol>
);

const IconListBlockContent = ({ items }: { items: Array<{ icon: string; title: string; description: string }> }) => (
  <ul className="flex flex-col gap-3">
    {items.map((item) => (
      <li key={item.title} className="flex items-start gap-3 rounded-lg border border-neutral-mid-gray p-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-light" aria-hidden>
          <Icon name={item.icon} size="sm" className="text-orange" />
        </span>
        <div className="flex flex-col gap-0.5">
          <Typography variant="action">{item.title}</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            {item.description}
          </Typography>
        </div>
      </li>
    ))}
  </ul>
);

const VideoBlockContent = ({ title, subtitle, duration, href }: { title: string; subtitle?: string; duration?: string; href?: string }) => {
  /*
   * A still, not a player: fixed 210px card, 10px inset, play mark 16px above the title, duration
   * chipped into the bottom-right corner. Bold PlayCircle in white gives the design's white disc with
   * the triangle knocked out of it, so the card colour shows through rather than a second fill.
   *
   * The card colour has no theme token — it is darker than anything in the palette — so it is written
   * as a literal. Worth a token if a second surface ever needs it.
   */
  const poster = (
    <div className="relative flex h-[210px] w-full flex-col items-center justify-center rounded-lg bg-[#2D0A3E] p-2.5">
      <Icon name="PlayCircle" variant="Bold" size={64} className="mb-4 text-white" aria-hidden />
      <Typography variant="action" className="text-white">
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="paragraph" className="px-4 text-center text-white">
          {subtitle}
        </Typography>
      )}
      {duration && (
        <span className="absolute bottom-2.5 right-2.5 rounded bg-black px-2 py-1">
          <Typography variant="small" className="text-white">
            {duration}
          </Typography>
        </span>
      )}
    </div>
  );

  // No link until the video is published. An unpublished video leaves the rest of the slide intact
  // rather than rendering a control that goes nowhere.
  if (!href) return poster;

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
      {poster}
    </a>
  );
};

export const TourBlockContent = ({ block }: { block: TourBlock }) => {
  switch (block.type) {
    case "paragraph":
      return <ParagraphBlockContent text={block.text} />;
    case "callout":
      return <CalloutBlockContent text={block.text} />;
    case "checklist":
      return <ChecklistBlockContent items={block.items} />;
    case "numbered":
      return <NumberedBlockContent items={block.items} />;
    case "iconlist":
      return <IconListBlockContent items={block.items} />;
    case "video":
      return <VideoBlockContent title={block.title} subtitle={block.subtitle} duration={block.duration} href={block.href} />;
  }
};
